/**
 * PHOTOS §4, the decisions behind a picture, kept apart from the canvas so they run in node: which pixels, which
 * quality, which id. The canvas itself is `canvasEncode.ts`, and does nothing but what these say.
 */

import { MAX_PHOTO_BYTES, PHOTO_H, PHOTO_W } from './types';

/** The post target is the picture plus a 1-texel border on every side (`RenderPipeline`, `frameSave.ts`). */
const TARGET_W = PHOTO_W + 2;
const TARGET_H = PHOTO_H + 2;

/**
 * The game's own frame as an image: `RenderPipeline.readPixels` (RGBA8, bottom row first, border included) cropped
 * and flipped exactly as `src/app/frameSave.ts` does, top row first, fully opaque. Null for a buffer of another size.
 */
export function cropFrame(src: Uint8Array): Uint8ClampedArray | null {
  if (src.length !== TARGET_W * TARGET_H * 4) return null;
  const out = new Uint8ClampedArray(PHOTO_W * PHOTO_H * 4);
  for (let y = 0; y < PHOTO_H; y++) {
    // readPixels is bottom-up; skip the 1-texel border.
    const row = (PHOTO_H - y) * TARGET_W + 1;
    for (let x = 0; x < PHOTO_W; x++) {
      const i = (row + x) * 4;
      const o = (y * PHOTO_W + x) * 4;
      out[o] = src[i] ?? 0;
      out[o + 1] = src[i + 1] ?? 0;
      out[o + 2] = src[i + 2] ?? 0;
      out[o + 3] = 255;
    }
  }
  return out;
}

/** The first JPEG quality tried; each refusal to fit steps down by `JPEG_QUALITY_STEP`. */
export const JPEG_QUALITY_START = 0.85;
export const JPEG_QUALITY_STEP = 0.1;
/** Below this the picture is mud: better none at all. */
export const JPEG_QUALITY_FLOOR = 0.4;

/** Every quality the encoder may try, best first: 0.85, 0.75, 0.65, 0.55, 0.45. */
export function jpegQualities(): number[] {
  const out: number[] = [];
  for (let k = 0; ; k++) {
    // Rounded to hundredths, so 0.85 - 0.1 * 4 is 0.45 and not 0.44999999999999996.
    const q = Math.round((JPEG_QUALITY_START - k * JPEG_QUALITY_STEP) * 100) / 100;
    if (q < JPEG_QUALITY_FLOOR) return out;
    out.push(q);
  }
}

/**
 * The best-quality JPEG no heavier than `max` bytes, or null: when none fits above the floor, or the encoder
 * could not encode at all (it answers null). `encode` is `canvas.toBlob` in the browser and a double under test.
 */
export async function encodeWithin(
  encode: (quality: number) => Promise<Blob | null>,
  max = MAX_PHOTO_BYTES,
): Promise<Blob | null> {
  for (const quality of jpegQualities()) {
    const jpeg = await encode(quality);
    if (jpeg === null) return null;
    if (jpeg.size <= max) return jpeg;
  }
  return null;
}

const ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
/** Long enough that two devices under one code never mint the same id (36^20, about 2^103). */
export const PHOTO_ID_LENGTH = 20;

/** A new photo id: `PHOTO_ID_LENGTH` characters of `[a-z0-9]` from `randomBytes` (`crypto.getRandomValues`). */
export function newPhotoId(randomBytes: (count: number) => Uint8Array): string {
  let id = '';
  // `% 36` leans very slightly towards the first four characters: irrelevant for a name, never a secret.
  for (const byte of randomBytes(PHOTO_ID_LENGTH)) id += ID_ALPHABET[byte % ID_ALPHABET.length] ?? 'a';
  return id.slice(0, PHOTO_ID_LENGTH).padEnd(PHOTO_ID_LENGTH, 'a');
}

/** Entropy for `newPhotoId` in the browser. */
export function cryptoBytes(count: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(count));
}
